import React, {forwardRef, useRef} from 'react'
import {
  Dialog as RadixDialogPrimitiveRoot,
  DialogTrigger as RadixDialogPrimitiveTrigger,
  DialogPortal as RadixDialogPrimitivePortal,
  DialogOverlay as RadixDialogPrimitiveOverlay,
  DialogContent as RadixDialogPrimitiveContent,
  DialogClose as RadixDialogPrimitiveClose,
  DialogTitle as RadixDialogPrimitiveTitle,
  DialogDescription as RadixDialogPrimitiveDescription
} from '@radix-ui/react-dialog'

import {
  DialogProps,
  DialogTriggerProps,
  DialogPortalProps,
  DialogOverlayProps,
  DialogContentProps,
  DialogCloseProps,
  DialogTitleProps,
  DialogDescriptionProps
} from './types'

import {useRefObjectAsForwardedRef} from '../..'

/**
 *
 * Contains all the parts of a dialog.
 *
 */
const Root = ({children, ...props}: DialogProps) => {
  return <RadixDialogPrimitiveRoot {...props}>{children}</RadixDialogPrimitiveRoot>
}

/**
 *
 * The button that opens the dialog.
 *
 */
const Trigger = forwardRef(({children, ...props}: DialogTriggerProps, forwardedRef) => {
  const innerRef = useRef<HTMLButtonElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <RadixDialogPrimitiveTrigger ref={innerRef} {...props}>
      {children}
    </RadixDialogPrimitiveTrigger>
  )
})

/**
 *
 * When used, portals your overlay and content parts into the `body`.
 *
 */
const Portal = ({children, ...props}: DialogPortalProps) => {
  return <RadixDialogPrimitivePortal {...props}>{children}</RadixDialogPrimitivePortal>
}

/**
 *
 * A layer that covers the inert portion of the view when the dialog is open.
 *
 */
const Overlay = forwardRef(({children, ...props}: DialogOverlayProps, forwardedRef) => {
  const innerRef = useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <RadixDialogPrimitiveOverlay ref={innerRef} {...props}>
      {children}
    </RadixDialogPrimitiveOverlay>
  )
})

/**
 *
 * A layer that covers the inert portion of the view when the dialog is open.
 *
 */
const Content = forwardRef(({children, ...props}: DialogContentProps, forwardedRef) => {
  const innerRef = useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <RadixDialogPrimitiveContent ref={innerRef} {...props}>
      {children}
    </RadixDialogPrimitiveContent>
  )
})

/**
 * The button that closes the dialog.
 */
const Close = forwardRef(({children, ...props}: DialogCloseProps, forwardedRef) => {
  const innerRef = useRef<HTMLButtonElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <RadixDialogPrimitiveClose ref={innerRef} {...props}>
      {children}
    </RadixDialogPrimitiveClose>
  )
})

/**
 * An accessible title to be announced when the dialog is opened.
 */
const Title = forwardRef(({children, ...props}: DialogTitleProps, forwardedRef) => {
  const innerRef = useRef<HTMLHeadingElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <RadixDialogPrimitiveTitle ref={innerRef} {...props}>
      {children}
    </RadixDialogPrimitiveTitle>
  )
})

/**
 * An optional accessible description to be announced when the dialog is opened.
 */
const Description = forwardRef(({children, ...props}: DialogDescriptionProps, forwardedRef) => {
  const innerRef = useRef<HTMLParagraphElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <RadixDialogPrimitiveDescription ref={innerRef} {...props}>
      {children}
    </RadixDialogPrimitiveDescription>
  )
})
// exports
Root.displayName = 'Dialog'
Trigger.displayName = 'Trigger'
Portal.displayName = 'Portal'
Overlay.displayName = 'Overlay'
Content.displayName = 'Content'
Close.displayName = 'Close'
Title.displayName = 'Title'
Description.displayName = 'Description'

/** A window overlaid on either the primary window or another dialog window, rendering the content underneath inert. */
export default Object.assign(Root, {Trigger, Portal, Overlay, Content, Close, Title, Description})
